/**
 * Where entries come from: the implicit manual source, ICS feeds, and collect
 * links, plus the queue of submissions waiting for a yes or no.
 *
 * This is a *section*, not a panel of its own any more: the Circles panel is
 * where a calendar is organised, and where entries arrive from is part of that.
 * {@link sourcesSection} builds the block and hands back the element plus the
 * way to redraw it, so whoever mounts it decides where it goes. The two dialogs
 * that add a source and the controls on a collect link are exported on their
 * own, because the empty ring's card and the Share dialog offer them too.
 */
import { createSourceSchema, type CalEvent, type PendingEntry, type Source, type Tag } from '@circle/shared';
import { api, imageUrl } from '../app/api';
import { validate } from '../app/validate';
import { MONTH_SHORT } from '../core/format';
import { button, copyRow, el, selectField, setChildren, textField, withBusy, type Child } from './dom';
import { confirmDialog, openDialog } from './dialog';
import { toast, toastError } from './toast';
import { SOURCE_COLOURS, nextColour } from './colours';
import type { PendingQueue } from './pending';

export interface SourcesSectionOptions {
  calendarId: string;
  /** Read live, not captured: `reload()` replaces the page’s arrays wholesale. */
  sources: () => Source[];
  events: () => CalEvent[];
  /** The circles, for the per-source "Default circle" select. Absent: no select. */
  tags?: () => Tag[];
  /** The page's queue of collect submissions; the section draws and drains it. */
  pending: PendingQueue;
  /** Refetches the bundle after a change that can touch many entries. */
  reload: () => Promise<void>;
}

export interface SourcesSection {
  root: HTMLElement;
  repaint: () => void;
  /** Stop listening to the queue. */
  destroy: () => void;
}

/** What the dialogs and the link controls need of the page. */
export interface SourceActions {
  calendarId: string;
  sources: () => Source[];
  reload: () => Promise<void>;
  /** The source was made or changed and the bundle is back. */
  onDone?: () => void;
}

export function relativeTime(iso: string | undefined, now = Date.now()): string {
  if (!iso) return 'never';
  const then = Date.parse(iso);
  if (!Number.isFinite(then)) return 'never';
  const seconds = Math.max(0, Math.round((now - then) / 1000));
  if (seconds < 60) return 'just now';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days} d ago`;
  return new Date(then).toLocaleDateString();
}

export function collectLink(token: string): string {
  return `${location.origin}/collect/${token}`;
}

export function kindLabel(kind: Source['kind']): string {
  return kind === 'manual' ? 'Added by hand' : kind === 'ics' ? 'Feed' : 'Collect link';
}

/**
 * A collect link's address and the way to replace it. The old link stops
 * working the moment a new one is made, which is why it asks first.
 */
export function collectLinkControls(
  source: Source,
  actions: Pick<SourceActions, 'calendarId'> & { onUpdated: (source: Source) => void },
): Child[] {
  if (source.kind !== 'collect' || !source.collectToken) return [];
  return [
    copyRow(collectLink(source.collectToken), 'Copy link'),
    el(
      'div',
      { class: 'row' },
      button(
        'Replace link',
        async () => {
          const yes = await confirmDialog({
            title: `Replace the collect link for ${source.name}?`,
            description:
              'The old link stops working straight away. Anyone still holding it will need the new one.',
            confirmLabel: 'Replace link',
          });
          if (!yes) return;
          try {
            const updated = await api.rotateCollectToken(actions.calendarId, source.id);
            toast('New collect link ready.');
            actions.onUpdated(updated);
          } catch (err) {
            toastError(err);
          }
        },
        { class: 'btn--small btn--quiet', 'data-role': 'rotate-collect' },
      ),
    ),
  ];
}

export function openIcsDialog(actions: SourceActions): void {
  const name = textField({ label: 'Name', name: 'name', placeholder: 'Family birthdays', required: true });
  const url = textField({
    label: 'Feed address',
    name: 'url',
    placeholder: 'https://calendar.example.com/birthdays.ics',
    hint: 'A public webcal or https .ics address.',
  });
  const refresh = selectField({
    label: 'Check for changes',
    name: 'refreshHours',
    value: '24',
    options: [
      { value: '6', label: 'Every 6 hours' },
      { value: '24', label: 'Once a day' },
      { value: '168', label: 'Once a week' },
    ],
  });
  const colour = el('input', {
    type: 'color',
    value: nextColour(SOURCE_COLOURS, actions.sources().map((s) => s.colour)),
    'aria-label': 'Colour',
  });

  const handle = openDialog({
    title: 'Subscribe to a feed',
    description: 'The feed owns the names and the dates and refreshes itself; the photos and colours are yours.',
    body: [
      name.root,
      url.root,
      el('div', { class: 'field-row' }, refresh.root, el('div', { class: 'field' }, el('label', { text: 'Colour' }), colour)),
    ],
  });
  const cancel = button('Cancel', () => handle.close());
  const save = button(
    'Add feed',
    () =>
      void withBusy(save, async () => {
        const raw = {
          kind: 'ics' as const,
          name: name.input.value.trim(),
          colour: colour.value,
          url: url.input.value.trim().replace(/^webcal:/i, 'https:'),
          refreshHours: Number(refresh.input.value),
        };
        const result = validate(createSourceSchema, raw);
        name.setError(result.ok ? null : (result.errors.name ?? null));
        url.setError(result.ok ? null : (result.errors.url ?? null));
        if (!result.ok) return;
        try {
          await api.createSource(actions.calendarId, raw);
          handle.close();
          await actions.reload();
          toast(`Added ${raw.name}. Refresh it to pull the entries in.`);
          actions.onDone?.();
        } catch (err) {
          toastError(err);
        }
      }),
    { class: 'btn--primary' },
  );
  handle.foot.append(el('span', { class: 'grow' }), cancel, save);
  handle.root.appendChild(handle.foot);
  name.input.focus();
}

export function openCollectDialog(actions: SourceActions): void {
  const name = textField({ label: 'Name', name: 'name', placeholder: 'Studio crew', required: true });
  const colour = el('input', {
    type: 'color',
    value: nextColour(SOURCE_COLOURS, actions.sources().map((s) => s.colour)),
    'aria-label': 'Colour',
  });
  const handle = openDialog({
    title: 'Create a collect link',
    description:
      'You get a link to share. Whoever opens it can add their name and birthday; nothing reaches the ring until you approve it.',
    body: [name.root, el('div', { class: 'field' }, el('label', { text: 'Colour' }), colour)],
  });
  const cancel = button('Cancel', () => handle.close());
  const save = button(
    'Create link',
    () =>
      void withBusy(save, async () => {
        const raw = { kind: 'collect' as const, name: name.input.value.trim(), colour: colour.value };
        const result = validate(createSourceSchema, raw);
        name.setError(result.ok ? null : (result.errors.name ?? null));
        if (!result.ok) return;
        try {
          await api.createSource(actions.calendarId, raw);
          handle.close();
          await actions.reload();
          toast('Collect link ready to share.');
          actions.onDone?.();
        } catch (err) {
          toastError(err);
        }
      }),
    { class: 'btn--primary' },
  );
  handle.foot.append(el('span', { class: 'grow' }), cancel, save);
  handle.root.appendChild(handle.foot);
  name.input.focus();
}

export function sourcesSection(options: SourcesSectionOptions): SourcesSection {
  const { calendarId, pending } = options;
  const root = el('div', { class: 'stack sources' });

  const repaintBody = (): void => {
    // The queue first: it is the one thing here that is waiting on the owner,
    // and at the bottom of the panel it went unread.
    const waiting = pendingSection();
    setChildren(root, [
      waiting,
      waiting ? el('hr', { class: 'rule' }) : null,
      sourceList(),
      el('hr', { class: 'rule' }),
      addRow(),
    ]);
  };

  const unwatch = pending.watch(() => {
    if (root.isConnected) repaintBody();
  });

  function countFor(sourceId: string): number {
    return options.events().filter((e) => e.sourceId === sourceId).length;
  }

  function sourceList(): HTMLElement {
    const list = el('ul', { class: 'list' });
    for (const source of options.sources()) {
      list.appendChild(sourceRow(source));
    }
    return list;
  }

  function sourceRow(source: Source): HTMLElement {
    const count = countFor(source.id);
    const meta: string[] = [`${count} ${count === 1 ? 'entry' : 'entries'}`];
    if (source.kind === 'ics') meta.push(`refreshed ${relativeTime(source.lastFetchedAt)}`);

    const actions: Child[] = [];
    if (source.kind === 'ics') {
      const refresh = button(
        'Refresh now',
        () =>
          void withBusy(refresh, async () => {
            try {
              const result = await api.refreshSource(calendarId, source.id);
              await options.reload();
              // A refresh that reaches the worker and then fails upstream comes
              // back 200 with `lastError` set. It is still a failure, and used
              // to be announced as a success while the panel quietly grew a red
              // notice further down.
              if (result.source.lastError) {
                toast(`Could not refresh ${source.name}: ${result.source.lastError}`, 'error', 7000);
              } else {
                toast(`Refreshed ${source.name}.`);
              }
              repaintBody();
            } catch (err) {
              toastError(err, `Could not refresh ${source.name}.`);
            }
          }),
        { class: 'btn--small' },
      );
      actions.push(refresh);
    }
    if (source.kind !== 'manual') {
      actions.push(
        button('Remove', () => void removeSource(source), { class: 'btn--small btn--quiet' }),
      );
    }

    const details: Child[] = [];
    const circle = defaultCircleField(source);
    if (circle) details.push(circle);
    if (source.kind === 'ics' && source.url) {
      details.push(el('div', { class: 'list__meta mono', text: source.url }));
    }
    if (source.kind === 'ics' && source.lastError) {
      details.push(
        el('div', { class: 'notice notice--alert small', text: `Last refresh failed: ${source.lastError}` }),
      );
    }
    if (source.kind === 'collect' && source.collectToken) {
      details.push(
        el('div', { class: 'list__meta', text: 'Anyone with this link can add themselves:' }),
        ...collectLinkControls(source, {
          calendarId,
          onUpdated: (updated) => {
            Object.assign(source, updated);
            repaintBody();
          },
        }),
      );
    }

    return el(
      'li',
      {},
      el('span', { class: 'swatch', style: `background:${source.colour};margin-top:6px` }),
      el(
        'div',
        { class: 'list__main stack', style: 'gap:6px' },
        el('div', { class: 'list__name', text: source.name }),
        el('div', { class: 'list__meta', text: `${kindLabel(source.kind)}, ${meta.join(', ')}` }),
        ...details,
        actions.length ? el('div', { class: 'row' }, ...actions) : null,
      ),
    );
  }

  /**
   * Which circle a feed or a collect link files new entries into.
   *
   * It applies on insert and never on refresh (docs/CIRCLES.md §1): once an
   * entry is here, its circles are the owner's business, and a nightly refresh
   * has no opinion about them. The manual source has none — you pick the
   * circles as you type the entry in.
   */
  function defaultCircleField(source: Source): HTMLElement | null {
    const tags = options.tags?.() ?? [];
    if (source.kind === 'manual' || tags.length === 0) return null;
    const field = selectField({
      label: 'Default circle',
      name: `default-circle-${source.id}`,
      value: source.defaultTagId ?? '',
      hint: 'Applied to new entries as they arrive, not to the ones already here.',
      options: [
        { value: '', label: 'Everyone' },
        ...tags.map((tag) => ({ value: tag.id, label: tag.name })),
      ],
    });
    field.input.addEventListener('change', () => {
      const next = field.input.value || null;
      void (async () => {
        try {
          Object.assign(source, await api.patchSource(calendarId, source.id, { defaultTagId: next }));
          toast(next ? 'New entries from here land in that circle.' : 'New entries from here stay on the root.');
        } catch (err) {
          field.input.value = source.defaultTagId ?? '';
          toastError(err);
        }
      })();
    });
    return field.root;
  }

  async function removeSource(source: Source): Promise<void> {
    const count = countFor(source.id);
    let keep = true;
    const handle = openDialog({
      title: `Remove “${source.name}”?`,
      description:
        count > 0
          ? `${count} ${count === 1 ? 'entry comes' : 'entries come'} from this source. Decide what happens to them.`
          : 'This source has no entries yet.',
      body:
        count > 0
          ? [
              el(
                'div',
                { class: 'radio-list' },
                choice('keep', 'Keep them as my own entries', 'They move to the manual source and stop updating.', true, (v) => (keep = v)),
                choice('drop', 'Delete them too', 'They disappear from the ring.', false, (v) => (keep = !v)),
              ),
            ]
          : [],
    });
    const cancel = button('Cancel', () => handle.close());
    const confirm = button(
      'Remove source',
      () =>
        void withBusy(confirm, async () => {
          try {
            await api.deleteSource(calendarId, source.id, keep && count > 0);
            handle.close();
            await options.reload();
            toast(`Removed ${source.name}.`);
            repaintBody();
          } catch (err) {
            toastError(err);
          }
        }),
      { class: 'btn--danger' },
    );
    handle.foot.append(el('span', { class: 'grow' }), cancel, confirm);
    handle.root.appendChild(handle.foot);
  }

  function choice(
    id: string,
    label: string,
    hint: string,
    checked: boolean,
    onPick: (chosen: boolean) => void,
  ): HTMLElement {
    const input = el('input', { type: 'radio', name: 'keep-events', value: id, checked });
    input.addEventListener('change', () => onPick(input.checked));
    return el(
      'label',
      {},
      input,
      el('span', {}, el('b', { text: label }), el('small', { text: hint })),
    );
  }

  const actions: SourceActions = {
    calendarId,
    sources: options.sources,
    reload: options.reload,
    onDone: repaintBody,
  };

  function addRow(): HTMLElement {
    return el(
      'div',
      { class: 'stack' },
      el('div', { class: 'section__head' }, el('h2', { text: 'Add a source' })),
      el(
        'div',
        { class: 'row' },
        button('Subscribe to a feed', () => openIcsDialog(actions), { class: 'btn--small' }),
        button('Create a collect link', () => openCollectDialog(actions), { class: 'btn--small' }),
      ),
      el('div', {
        class: 'small muted',
        text: 'A feed keeps itself up to date from a calendar you already have. A collect link lets people add their own birthday.',
      }),
    );
  }

  /**
   * "Waiting for you". Drawn whenever there is something to say — entries,
   * a failure, or the first load — and otherwise only where a collect link
   * exists, since without one nothing can ever arrive here.
   */
  function pendingSection(): HTMLElement | null {
    const { entries, error } = pending.state;
    const hasCollect = options.sources().some((s) => s.kind === 'collect');
    if (entries && entries.length === 0 && !error && !hasCollect) return null;

    const head = el(
      'div',
      { class: 'section__head' },
      el('h2', { text: 'Waiting for you' }),
      entries ? el('span', { class: 'list__meta', text: `${entries.length}` }) : null,
    );

    if (error) {
      return el('div', { class: 'stack' }, head, el('div', { class: 'notice notice--alert', text: error }));
    }
    if (!entries) {
      return el('div', { class: 'stack' }, head, el('div', { class: 'empty' }, el('span', { class: 'spinner' })));
    }
    if (entries.length === 0) {
      return el(
        'div',
        { class: 'stack' },
        head,
        el('div', { class: 'empty', text: 'Nothing submitted yet. Share a collect link and entries land here.' }),
      );
    }

    const list = el('ul', { class: 'list', 'data-role': 'pending' });
    for (const entry of entries) list.appendChild(pendingRow(entry));
    return el('div', { class: 'stack' }, head, list);
  }

  function pendingRow(entry: PendingEntry): HTMLElement {
    const act = async (action: 'approve' | 'reject'): Promise<void> => {
      try {
        await api.actOnPending(calendarId, entry.id, action);
        if (action === 'approve') await options.reload();
        toast(action === 'approve' ? `Added ${entry.title} to the ring.` : `Rejected ${entry.title}.`);
        // The queue tells everyone who is watching, this section included.
        pending.remove(entry.id);
      } catch (err) {
        toastError(err);
      }
    };

    const date = `${entry.day} ${MONTH_SHORT[entry.month - 1] ?? ''}${entry.year ? `, ${entry.year}` : ''}`;
    return el(
      'li',
      {},
      entry.imageKey
        ? el('img', { class: 'avatar', style: 'width:38px;height:38px', src: imageUrl(entry.imageKey), alt: '' })
        : el('div', { class: 'avatar avatar--letter', style: 'width:38px;height:38px;font-size:15px', text: entry.title.charAt(0).toUpperCase() }),
      el(
        'div',
        { class: 'list__main' },
        el('div', { class: 'list__name', text: entry.title }),
        el('div', { class: 'list__meta', text: date }),
      ),
      el(
        'div',
        { class: 'row' },
        button('Add', () => void act('approve'), { class: 'btn--small' }),
        button('Reject', () => void act('reject'), { class: 'btn--small btn--quiet' }),
      ),
    );
  }

  repaintBody();

  return { root, repaint: repaintBody, destroy: unwatch };
}
